import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { useSelector } from 'react-redux';
import { HbrCard, HbrIcon, HbrSpinner } from '@harbor/elements-react-standalone';
import { arrowLeft } from '@harbor/elements/icons';
import { fetchItems, postItem, putItem } from '../../../support/queries';
import { useAuthenticateUser } from '../../../hooks/useAuthenticateUser';
import { RootState } from '../../../support/store';
import StbButton from '../../security-toolbox-ui/StbButton';
import { moodleFetch, MoodleInstance } from '../../../hooks/useMoodleHooks';
import JourneyMapViewer from './JourneyMapViewer';
import { RelatedArchitectures } from '../../Common/RelatedByTag';
import JourneyMapMoodleCourseModalContent from './JourneyMapMoodleCourseModalContent';
import {
  evaluateAutoCompleteResponse,
  normalizeJourneyMapCompletionConfig,
  resolveJourneyMapCompletion,
} from '../../../support/journeyMapCompletion';
import {
  getContentOptionLabel,
  getContentRefTypeOption,
  hasToolboxDbRecord,
} from '../../../support/journeyMap/contentSources';
import {
  AWAITING_CONFIRMATION_MESSAGE,
  UNCONFIRMED_MESSAGE,
  describeLaunchSyncState,
  recordJourneyMapItemLaunch,
  shouldTrackExternalLaunch,
} from '../../../support/journeyMap/externalLaunch';

type JourneyMapDetailData = {
  journeyMap: any | null;
  items: any[];
  links: any[];
};

type CompletionResolutionResult = {
  passed: boolean;
  reason?: string;
  actualValue?: any;
};

const COMPLETION_SOURCE_EXTERNAL_SYNC = 'external_sync';
const PATH_STATE_COMPLETED = 'completed';
const PATH_STATE_PENDING_VALIDATION = 'pending_validation';

const asArray = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const extractId = (value: any): string | null => {
  if (value == null) return null;
  if (typeof value === 'object') return value.id != null ? String(value.id) : null;
  return String(value);
};

const resolveSourceId = (link: any): string | null =>
  extractId(link?.from_item) ||
  extractId(link?.source_item) ||
  extractId(link?.source) ||
  extractId(link?.from) ||
  extractId(link?.from_journey_map_item) ||
  extractId(link?.source_journey_map_item) ||
  null;

const resolveTargetId = (link: any): string | null =>
  extractId(link?.to_item) ||
  extractId(link?.target_item) ||
  extractId(link?.target) ||
  extractId(link?.to) ||
  extractId(link?.to_journey_map_item) ||
  extractId(link?.target_journey_map_item) ||
  null;

const toNumber = (value: any, fallback = 0): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const withTrailingSlash = (value: string): string => (value.endsWith('/') ? value : `${value}/`);

const loadJourneyMap = async (id: string | undefined): Promise<JourneyMapDetailData | null> => {
  if (!id) return null;

  const [journeyMapResponse, itemsResponse] = await Promise.all([
    fetchItems(`db/journey-map/${id}/`, {}, { includePageSize: false }),
    fetchItems(
      'db/journey-map-item/',
      { journey_map: id, size: 200 },
      { includePageSize: false }
    ),
  ]);

  const journeyMap = journeyMapResponse || null;
  const rawItems = asArray(itemsResponse);

  const referenceLabelMap: Record<string, string> = {};

  const uniqueRefs = new Map<
    string,
    {
      contentRefType: string;
      contentRefId: string;
      endpoint: string;
      displayFields: string[];
    }
  >();

  rawItems
    .filter((item: any) => !item?.isDeleted && item?.content_ref_id && item?.content_ref_type)
    .forEach((item: any) => {
      const contentRefType = String(item.content_ref_type || '').trim();
      const contentRefId = String(item.content_ref_id || '').trim();
      if (!contentRefType || !contentRefId) return;

      const option = getContentRefTypeOption(contentRefType);
      if (!option?.endpoint) return;

      const endpoint = withTrailingSlash(option.endpoint);
      const displayFields = option.displayFields || ['title', 'name', 'id'];
      const compositeKey = `${contentRefType}::${contentRefId}`;

      if (!uniqueRefs.has(compositeKey)) {
        uniqueRefs.set(compositeKey, {
          contentRefType,
          contentRefId,
          endpoint,
          displayFields,
        });
      }
    });

  await Promise.all(
    Array.from(uniqueRefs.values()).map(async (ref) => {
      try {
        const row = await fetchItems(
          `${ref.endpoint}${ref.contentRefId}/`,
          {},
          { includePageSize: false }
        );

        if (!row) return;

        const compositeKey = `${ref.contentRefType}::${ref.contentRefId}`;
        referenceLabelMap[compositeKey] = getContentOptionLabel(row, ref.displayFields);
      } catch {
        //
      }
    })
  );

  const items = rawItems.map((item: any) => {
    const contentRefType = String(item?.content_ref_type || '').trim();
    const contentRefId = String(item?.content_ref_id || '').trim();
    const compositeKey = `${contentRefType}::${contentRefId}`;

    return {
      ...item,
      position_x: toNumber(item?.position_x ?? item?.metadata?.position_x, 0),
      position_y: toNumber(item?.position_y ?? item?.metadata?.position_y, 0),
      resolvedReferencedTitle: referenceLabelMap[compositeKey] || '',
    };
  });

  const itemIds = new Set(items.map((item) => String(item?.id)).filter(Boolean));

  let rawLinks: any[] = [];
  try {
    rawLinks = asArray(
      await fetchItems(
        'db/journey-map-item-link/',
        { journey_map: id, size: 200 },
        { includePageSize: false }
      )
    );
  } catch {
    rawLinks = [];
  }

  const links = rawLinks.filter((link) => {
    const sourceId = resolveSourceId(link);
    const targetId = resolveTargetId(link);

    return (
      !!sourceId &&
      !!targetId &&
      itemIds.has(String(sourceId)) &&
      itemIds.has(String(targetId))
    );
  });

  return { journeyMap, items, links };
};

const getValueAtPath = (obj: any, path?: string): any => {
  if (!obj || !path) return undefined;
  return String(path)
    .split('.')
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
};

const fetchReferencedRecord = async (item: any): Promise<any | null> => {
  if (!item?.content_ref_type || !item?.content_ref_id) return null;

  // Types whose id is not a toolbox_db primary key have no record to fetch — a `moodle_course`
  // id would otherwise be sent to `db/moodle-course/{id}/`.
  if (!hasToolboxDbRecord(item.content_ref_type)) return null;

  const resourceName = String(item.content_ref_type).replace(/_/g, '-');
  try {
    return await fetchItems(`db/${resourceName}/${item.content_ref_id}/`, {}, { includePageSize: false });
  } catch {
    return null;
  }
};

const isTaskItem = (item: any) => String(item?.item_type || '').trim().toLowerCase() === 'task';

const resolveJourneyMapMoodleInstance = (item: any): MoodleInstance => {
  const provider = String(item?.provider || '').toLowerCase();
  const providerConfigInstance = String(item?.provider_config?.instance || '').toLowerCase();
  const providerMode = String(item?.provider_config?.mode || '').toLowerCase();
  const metadataInstance = String(item?.metadata?.instance || '').toLowerCase();

  if (
    provider.includes('cyber') ||
    provider.includes('internal') ||
    providerConfigInstance === 'internal' ||
    providerMode === 'internal' ||
    metadataInstance === 'internal'
  ) {
    return 'internal';
  }

  return 'external';
};

function getLearningCompletionStorageKey(instance: 'external' | 'internal', courseId: number | string) {
  return `learning:localCompletion:moodle:${instance}:${courseId}`;
}

const resolveJourneyMapCourseId = async (item: any): Promise<number | null> => {
  const directCourseId =
    item?.provider_config?.moodle?.course_id ??
    item?.metadata?.selected_deployed_moodle_id ??
    null;

  if (directCourseId != null && String(directCourseId).trim() !== '') {
    const n = Number(directCourseId);
    if (Number.isFinite(n)) return n;
  }

  const referenced = await fetchReferencedRecord(item);

  const referencedCourseId =
    referenced?.deployed_moodle_id ??
    referenced?.staged_moodle_id ??
    null;

  if (referencedCourseId != null && String(referencedCourseId).trim() !== '') {
    const n = Number(referencedCourseId);
    if (Number.isFinite(n)) return n;
  }

  return null;
};

const isCourseProvider = (item: any) => {
  const provider = String(item?.provider || '').toLowerCase();
  return provider.includes('moodle') || provider.includes('cyber');
};

const hasLocalCourseCompletion = async (item: any): Promise<boolean> => {
  if (!isCourseProvider(item)) return false;

  const instance = resolveJourneyMapMoodleInstance(item);
  const courseId = await resolveJourneyMapCourseId(item);

  if (!courseId) return false;

  try {
    const raw = localStorage.getItem(getLearningCompletionStorageKey(instance, courseId));
    if (!raw) return false;

    const parsed = JSON.parse(raw);
    const itemIds = Array.isArray(parsed?.itemIds) ? parsed.itemIds : [];
    return itemIds.length > 0;
  } catch {
    return false;
  }
};

const fetchCompletionRuleResponse = async (
  endpoint: string,
  params: Record<string, any>,
  item: any
) => {
  const normalizedEndpoint = String(endpoint || '').replace(/^\/+/, '');

  if (normalizedEndpoint.startsWith('moodle/')) {
    const instance = resolveJourneyMapMoodleInstance(item);
    return moodleFetch<any>(normalizedEndpoint, params, {}, instance);
  }

  return fetchItems(normalizedEndpoint, params, { includePageSize: false });
};

const resolveCompletionForItem = async (
  item: any,
  email: string
): Promise<CompletionResolutionResult> => {
  if (!email) return { passed: false };
  if (isTaskItem(item)) return { passed: false };

  const normalizedConfig = normalizeJourneyMapCompletionConfig(
    item?.provider_config?.completion_tracking ?? item?.provider_config ?? null
  );

  const mode = normalizedConfig?.mode || null;
  if (mode !== 'auto_complete') {
    return { passed: false };
  }

  const providerName = item?.provider || null;
  if (!providerName) {
    return { passed: false };
  }

  let providers: any[] = [];
  try {
    providers = asArray(
      await fetchItems('db/journey-map-completion-provider/', {}, { includePageSize: false })
    );
  } catch {
    providers = [];
  }

  const provider =
    providers.find((p) => {
      const displayName = String(p?.display_name || '').toLowerCase();
      const providerKey = String(p?.provider_key || '').toLowerCase();
      const itemProvider = String(providerName).toLowerCase();
      const normalizedItemProvider = itemProvider.replace(/\s+/g, '_');
      return (
        displayName === itemProvider ||
        providerKey === normalizedItemProvider ||
        providerKey === itemProvider
      );
    }) || null;

  if (!provider?.supports_auto_complete) {
    return { passed: false };
  }

  const autoCompleteType = normalizedConfig?.autoCompleteType || null;

  const rule =
    provider?.rules?.find(
      (r: any) => r?.auto_complete_type === autoCompleteType && r?.is_active !== false
    ) || null;

  if (!rule?.endpoint) {
    return { passed: false };
  }

  let referenceValue = getValueAtPath(item, provider.reference_field);

  if (referenceValue == null || referenceValue === '') {
    const referenced = await fetchReferencedRecord(item);
    referenceValue = getValueAtPath(referenced, provider.reference_field || 'id');
  }

  if (referenceValue == null || referenceValue === '') {
    return { passed: false };
  }

  const params: Record<string, any> = {
    [provider.email_param || 'email']: email,
    [rule.reference_param || 'course_id']: referenceValue,
  };

  try {
    const raw = await fetchCompletionRuleResponse(rule.endpoint, params, item);

    if (typeof raw?.passed === 'boolean') {
      const actualValue =
        raw?.score ??
        raw?.actualValue ??
        raw?.actual_value ??
        raw?.percent ??
        raw?.percentage ??
        undefined;

      if (raw.passed) {
        let reason = 'Completed by auto-complete rule.';

        if (autoCompleteType === 'assessment_score') {
          reason =
            actualValue != null
              ? `Completed by passing assessment with score of ${actualValue}.`
              : 'Completed by passing assessment.';
        } else if (autoCompleteType === 'course_completion') {
          reason = 'Completed by meeting course progress requirements.';
        }

        return {
          passed: true,
          reason,
          actualValue,
        };
      }

      if (autoCompleteType === 'course_completion' && isCourseProvider(item)) {
        const localComplete = await hasLocalCourseCompletion(item);

        if (localComplete) {
          return {
            passed: true,
            reason: 'Completed by meeting course progress requirements.',
            actualValue: 100,
          };
        }
      }

      return { passed: false };
    }

    const normalizedResponse = resolveJourneyMapCompletion(raw);
    const evaluation = evaluateAutoCompleteResponse(normalizedResponse, normalizedConfig);

    if (evaluation?.passed) {
      const actualValue = evaluation?.actualValue;
      let reason = 'Completed by auto-complete rule.';

      if (autoCompleteType === 'assessment_score') {
        reason =
          actualValue != null
            ? `Completed by passing assessment with score of ${actualValue}.`
            : 'Completed by passing assessment.';
      } else if (autoCompleteType === 'course_completion') {
        reason = 'Completed by meeting course progress requirements.';
      } else if (typeof evaluation?.reason === 'string' && evaluation.reason.trim()) {
        reason = evaluation.reason;
      }

      return {
        passed: true,
        reason,
        actualValue,
      };
    }

    if (autoCompleteType === 'course_completion' && isCourseProvider(item)) {
      const localComplete = await hasLocalCourseCompletion(item);

      if (localComplete) {
        return {
          passed: true,
          reason: 'Completed by meeting course progress requirements.',
          actualValue: 100,
        };
      }
    }

    return { passed: false };
  } catch (error) {
    console.warn('Journey map completion check failed for item', item?.id, error);

    if (autoCompleteType === 'course_completion' && isCourseProvider(item)) {
      const localComplete = await hasLocalCourseCompletion(item);

      if (localComplete) {
        return {
          passed: true,
          reason: 'Completed by meeting course progress requirements.',
          actualValue: 100,
        };
      }
    }

    return { passed: false };
  }
};

const buildOutgoingLinksBySource = (links: any[]) => {
  const map = new Map<string, any[]>();

  links.forEach((link) => {
    const sourceId = resolveSourceId(link);
    if (!sourceId) return;

    const existing = map.get(String(sourceId)) || [];
    existing.push(link);
    map.set(String(sourceId), existing);
  });

  return map;
};

const collectDescendants = (
  startItemId: string,
  outgoingBySource: Map<string, any[]>,
  visited = new Set<string>(),
): Set<string> => {
  if (visited.has(startItemId)) return visited;
  visited.add(startItemId);

  const outgoing = outgoingBySource.get(String(startItemId)) || [];
  outgoing.forEach((link) => {
    const targetId = resolveTargetId(link);
    if (!targetId) return;
    collectDescendants(String(targetId), outgoingBySource, visited);
  });

  return visited;
};

const isAssignmentRowAssigned = (row: any): boolean => {
  const bucket = String(row?.assignment_bucket || '').trim().toLowerCase();

  if (typeof row?.is_assigned_state === 'boolean') {
    return row.is_assigned_state;
  }

  if (bucket) {
    return bucket === 'assigned' || bucket === 'completed';
  }

  if (typeof row?.is_active === 'boolean') {
    return row.is_active;
  }

  return true;
};

const isJourneyAccessibleAssignment = (row: any): boolean => {
  if (!row) return false;

  const bucket = String(row?.assignment_bucket || '').trim().toLowerCase();

  if (bucket === 'assigned' || bucket === 'completed') {
    return true;
  }

  if (typeof row?.is_completed === 'boolean' && row.is_completed) {
    return true;
  }

  if (typeof row?.is_assigned_state === 'boolean' && row.is_assigned_state) {
    return true;
  }

  return Boolean(row?.is_active);
};

const isItemProgressFullySynced = (
  row: any,
  expectedAssignmentId: number | string,
  expectedUserId: number | string,
  expectedCompletedStatusId: number | string | null
) => {
  if (!row) return false;

  const pathStateCompleted =
    String(row?.path_state || '').trim().toLowerCase() === PATH_STATE_COMPLETED;

  const hasCompletedDate = !!row?.completed_date;
  const assignmentMatches = String(row?.assignment) === String(expectedAssignmentId);
  const userMatches = String(row?.user) === String(expectedUserId);

  const sourceMatches =
    String(row?.completion_source || '').trim().toLowerCase() === COMPLETION_SOURCE_EXTERNAL_SYNC;

  const syncStatusMatches =
    String(row?.sync_status || '').trim().toLowerCase() === 'validated';

  const statusMatches =
    expectedCompletedStatusId == null
      ? false
      : String(row?.status) === String(expectedCompletedStatusId);

  return (
    pathStateCompleted &&
    hasCompletedDate &&
    assignmentMatches &&
    userMatches &&
    sourceMatches &&
    syncStatusMatches &&
    statusMatches
  );
};

const isProgressPendingValidation = (row: any) => {
  if (!row) return false;

  if (row?.is_pending_validation === true) return true;

  const pathState = String(row?.path_state || '').trim().toLowerCase();
  const syncStatus = String(row?.sync_status || '').trim().toLowerCase();

  // 'stale' is still pending: the launch was recorded but the nightly sync never confirmed it,
  // so the step is unfinished rather than resolved either way.
  return (
    pathState === PATH_STATE_PENDING_VALIDATION ||
    syncStatus === 'pending' ||
    syncStatus === 'stale'
  );
};

const getStatusReasonForItem = (item: any, row: any) => {
  if (!row) return '';

  const syncStatus = String(row?.sync_status || '').trim().toLowerCase();
  const pathState = String(row?.path_state || '').trim().toLowerCase();
  const taskSubmission = row?.task_submission || null;

  // Checked before the pending branch: a stale row is still pending, but the learner needs the
  // stronger message because nothing more is going to arrive on its own.
  if (syncStatus === 'stale') {
    if (String(item?.item_type || '').toLowerCase() === 'task') {
      return 'Your task submission is still awaiting validation. Reach out if it has been a while.';
    }

    return UNCONFIRMED_MESSAGE;
  }

  if (pathState === PATH_STATE_PENDING_VALIDATION || syncStatus === 'pending') {
    if (String(item?.item_type || '').toLowerCase() === 'task') {
      return 'Your task submission was saved and is awaiting validation.';
    }

    if (String(item?.provider || '').toLowerCase().includes('mindtickle')) {
      return AWAITING_CONFIRMATION_MESSAGE;
    }

    return 'This item is awaiting validation.';
  }

  if (syncStatus === 'no_match') {
    return 'No external completion match was found yet.';
  }

  if (syncStatus === 'validated') {
    return 'Validation completed successfully.';
  }

  if (taskSubmission?.id && !row?.completed_date) {
    return 'A task submission exists for this item.';
  }

  return '';
};

const JourneyMapDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { email } = useAuthenticateUser();
  const currentUserId = useSelector((state: RootState) => state.user?.userData?.id);
  const queryClient = useQueryClient();

  const [localCompletionByItemId, setLocalCompletionByItemId] = useState<Record<string, boolean>>(
    {}
  );
  const [localCompletionReasonByItemId, setLocalCompletionReasonByItemId] = useState<
    Record<string, string>
  >({});

  const [localPendingValidationByItemId, setLocalPendingValidationByItemId] = useState<
    Record<string, boolean>
  >({});
  const [localStatusReasonByItemId, setLocalStatusReasonByItemId] = useState<
    Record<string, string>
  >({});

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['journey-map-detail', id],
    queryFn: () => loadJourneyMap(id),
    ...{
        enabled: !!id,
        staleTime: 60_000,
      },
  });

  const journeyMap = data?.journeyMap || null;
  const items = useMemo(() => (Array.isArray(data?.items) ? data.items : []), [data]);
  const links = useMemo(() => (Array.isArray(data?.links) ? data.links : []), [data]);

  const assignmentQuery = useQuery({
    queryKey: ['journey-map-assignment-for-user', id, currentUserId],
    queryFn: async () => {
      if (!id || !currentUserId) return [];
      const rows = await fetchItems(
        'db/journey-map-assignment/',
        { journey_map: id, user: currentUserId },
        { includePageSize: false }
      );
      return asArray(rows);
    },
    ...{
        enabled: !!id && !!currentUserId,
        staleTime: 60_000,
      },
  });

  const assignmentRows = useMemo(
    () => (Array.isArray(assignmentQuery.data) ? assignmentQuery.data : []),
    [assignmentQuery.data]
  );

  const assignmentRecord = useMemo(() => {
    if (!assignmentRows.length) return null;

    const ranked = [...assignmentRows].sort((a: any, b: any) => {
      const aAssigned = isAssignmentRowAssigned(a) ? 1 : 0;
      const bAssigned = isAssignmentRowAssigned(b) ? 1 : 0;
      if (bAssigned !== aAssigned) return bAssigned - aAssigned;

      const aUpdated = Date.parse(String(a?.last_updated || a?.created || ''));
      const bUpdated = Date.parse(String(b?.last_updated || b?.created || ''));
      return (Number.isNaN(bUpdated) ? 0 : bUpdated) - (Number.isNaN(aUpdated) ? 0 : aUpdated);
    });

    return ranked[0] || null;
  }, [assignmentRows]);

  const isAssigned = useMemo(() => {
    return assignmentRows.some((row: any) => isJourneyAccessibleAssignment(row));
  }, [assignmentRows]);

  const itemStatusesQuery = useQuery({
    queryKey: ['journey-map-item-statuses'],
    queryFn: async () => {
      const rows = await fetchItems('db/journey-map-item-status/', {}, { includePageSize: false });
      return asArray(rows);
    },
    ...{
        staleTime: 300_000,
      },
  });

  const completedItemStatusId = useMemo(() => {
    const rows = Array.isArray(itemStatusesQuery.data) ? itemStatusesQuery.data : [];
    const completed = rows.find(
      (row: any) => String(row?.name || '').trim().toLowerCase() === 'completed'
    );
    return completed?.id ?? null;
  }, [itemStatusesQuery.data]);

  const itemProgressQuery = useQuery({
    queryKey: ['journey-map-item-progress', id, currentUserId, assignmentRecord?.id],
    queryFn: async () => {
      if (!currentUserId || !assignmentRecord?.id || items.length === 0) return [];
      // Every item's progress row must come back in one page: completion is derived
      // per item from these rows, so a row left off the page reads as "not complete"
      // and the journey silently regresses on reload. `includePageSize: false` sends
      // no `size`, which makes the backend fall back to its default page size (10) —
      // a map with more than 10 items then drops its highest-id progress rows (the
      // most recently written ones). Request an explicit large page so all rows load.
      const rows = await fetchItems(
        'db/journey-map-item-progress/',
        { assignment: assignmentRecord.id, user: currentUserId },
        { includePageSize: true, size: 1000 }
      );
      const validItemIds = new Set(items.map((item) => String(item.id)));
      return asArray(rows).filter((row: any) => validItemIds.has(String(row?.journey_map_item)));
    },
    ...{
        enabled: !!currentUserId && !!assignmentRecord?.id && items.length > 0,
        staleTime: 30_000,
      },
  });

  const itemProgressRows = useMemo(
    () => (Array.isArray(itemProgressQuery.data) ? itemProgressQuery.data : []),
    [itemProgressQuery.data]
  );

  const itemProgressByItemId = useMemo(() => {
    const map = new Map<string, any>();
    itemProgressRows.forEach((row: any) => {
      if (row?.journey_map_item != null) {
        map.set(String(row.journey_map_item), row);
      }
    });
    return map;
  }, [itemProgressRows]);

  const itemsWithProgress = useMemo(() => {
    return items.map((item) => {
      const row = itemProgressByItemId.get(String(item.id)) || null;
      return {
        ...item,
        progress: row,
        task_submission: item?.task_submission || row?.task_submission || null,
        path_state: row?.path_state ?? item?.path_state ?? null,
        sync_status: row?.sync_status ?? item?.sync_status ?? null,
        status_name: row?.status_name ?? item?.status_name ?? null,
        is_pending_validation:
          row?.is_pending_validation === true || isProgressPendingValidation(row),
      };
    });
  }, [items, itemProgressByItemId]);

  const persistedCompletionByItemId = useMemo(() => {
    const map: Record<string, boolean> = {};

    itemProgressRows.forEach((row: any) => {
      const itemId = String(row?.journey_map_item);
      const pathStateCompleted =
        String(row?.path_state || '').trim().toLowerCase() === PATH_STATE_COMPLETED;
      const statusNameCompleted =
        String(row?.status_name || '').trim().toLowerCase() === 'completed';
      const hasCompletedDate = !!row?.completed_date;

      map[itemId] = pathStateCompleted || statusNameCompleted || hasCompletedDate;
    });

    return map;
  }, [itemProgressRows]);

  const persistedPendingValidationByItemId = useMemo(() => {
    const map: Record<string, boolean> = {};

    itemProgressRows.forEach((row: any) => {
      const itemId = String(row?.journey_map_item);
      map[itemId] = isProgressPendingValidation(row);
    });

    return map;
  }, [itemProgressRows]);

  const syncStatusByItemId = useMemo(() => {
    const map: Record<string, string | null> = {};

    itemProgressRows.forEach((row: any) => {
      const itemId = String(row?.journey_map_item);
      map[itemId] = row?.sync_status ?? null;
    });

    return map;
  }, [itemProgressRows]);

  const completedItemIdSet = useMemo(() => {
    const set = new Set<string>();

    itemProgressRows.forEach((row: any) => {
      const itemId = String(row?.journey_map_item || '');
      if (!itemId) return;

      const pathStateCompleted =
        String(row?.path_state || '').trim().toLowerCase() === PATH_STATE_COMPLETED;
      const statusNameCompleted =
        String(row?.status_name || '').trim().toLowerCase() === 'completed';
      const hasCompletedDate = !!row?.completed_date;

      if (pathStateCompleted || statusNameCompleted || hasCompletedDate) {
        set.add(itemId);
      }
    });

    return set;
  }, [itemProgressRows]);

  const completionQuery = useQuery({
    queryKey: ['journey-map-completion', id, email, items.map((i) => i.id).join(',')],
    queryFn: async () => {
      const entries = await Promise.all(
        items.map(async (item) => {
          const result = await resolveCompletionForItem(item, email || '');
          return [String(item.id), result] as const;
        })
      );
      return Object.fromEntries(entries) as Record<string, CompletionResolutionResult>;
    },
    ...{
        enabled: !!email && items.length > 0,
        staleTime: 60_000,
      },
  });

  const completionByItemId = useMemo(() => {
    const merged: Record<string, boolean> = {};
    const allItemIds = new Set<string>([
      ...Object.keys(persistedCompletionByItemId || {}),
      ...Object.keys(completionQuery.data || {}),
      ...Object.keys(localCompletionByItemId || {}),
      ...items.map((item) => String(item.id)),
    ]);

    allItemIds.forEach((itemId) => {
      const persisted = !!persistedCompletionByItemId[itemId];
      const computed = !!(completionQuery.data || {})[itemId]?.passed;
      const local = !!localCompletionByItemId[itemId];

      merged[itemId] = local || persisted || computed;
    });

    return merged;
  }, [items, persistedCompletionByItemId, completionQuery.data, localCompletionByItemId]);

  const pendingValidationByItemId = useMemo(() => {
    const merged: Record<string, boolean> = {};
    const allItemIds = new Set<string>([
      ...Object.keys(persistedPendingValidationByItemId || {}),
      ...Object.keys(localPendingValidationByItemId || {}),
      ...items.map((item) => String(item.id)),
    ]);

    allItemIds.forEach((itemId) => {
      const isComplete = !!completionByItemId[itemId];
      const persisted = !!persistedPendingValidationByItemId[itemId];
      const local = !!localPendingValidationByItemId[itemId];

      merged[itemId] = !isComplete && (persisted || local);
    });

    return merged;
  }, [
    items,
    completionByItemId,
    persistedPendingValidationByItemId,
    localPendingValidationByItemId,
  ]);

  const completionReasonByItemId = useMemo(() => {
    const map: Record<string, string> = {
      ...localCompletionReasonByItemId,
    };

    items.forEach((item) => {
      const itemId = String(item.id);

      if (map[itemId]) return;

      const computed = completionQuery.data?.[itemId];
      const persistedRow = itemProgressByItemId.get(itemId);
      const isComplete = !!completionByItemId[itemId];

      if (!isComplete) {
        return;
      }

      if (computed?.reason) {
        map[itemId] = computed.reason;
        return;
      }

      const source = String(persistedRow?.completion_source || '').trim().toLowerCase();
      const overrideReason = String(persistedRow?.override_reason || '').trim();
      const evidence = persistedRow?.evidence || {};
      const score =
        persistedRow?.score ??
        evidence?.score ??
        evidence?.actualValue ??
        evidence?.actual_value ??
        null;

      if (overrideReason) {
        map[itemId] = overrideReason;
        return;
      }

      if (source === 'manager_approval') {
        map[itemId] = 'Completed by manager approval.';
        return;
      }

      if (source === 'manual') {
        map[itemId] = 'Completed manually.';
        return;
      }

      if (source === COMPLETION_SOURCE_EXTERNAL_SYNC) {
        const normalizedConfig = normalizeJourneyMapCompletionConfig(
          item?.provider_config?.completion_tracking ?? item?.provider_config ?? null
        );

        const autoCompleteType = normalizedConfig?.autoCompleteType || null;

        if (autoCompleteType === 'assessment_score') {
          map[itemId] =
            score != null
              ? `Completed by passing assessment with score of ${score}.`
              : 'Completed by passing assessment.';
          return;
        }

        if (autoCompleteType === 'course_completion') {
          map[itemId] = 'Completed by meeting course progress requirements.';
          return;
        }

        map[itemId] = 'Completed by external sync.';
        return;
      }

      map[itemId] = 'Completion has been satisfied for this journey item.';
    });

    return map;
  }, [
    items,
    completionQuery.data,
    itemProgressByItemId,
    completionByItemId,
    localCompletionReasonByItemId,
  ]);

  const statusReasonByItemId = useMemo(() => {
    const map: Record<string, string> = {
      ...localStatusReasonByItemId,
    };

    items.forEach((item) => {
      const itemId = String(item.id);
      if (map[itemId]) return;

      const row = itemProgressByItemId.get(itemId);
      const reason = getStatusReasonForItem(item, row);
      if (reason) {
        map[itemId] = reason;
      }
    });

    return map;
  }, [items, itemProgressByItemId, localStatusReasonByItemId]);

  const notApplicableByItemId = useMemo(() => {
    const map: Record<string, boolean> = {};

    items.forEach((item) => {
      map[String(item.id)] = false;
    });

    const outgoingBySource = buildOutgoingLinksBySource(links);

    for (const item of items) {
      const sourceId = String(item.id);
      const outgoing = outgoingBySource.get(sourceId) || [];
      if (!outgoing.length) continue;

      const testOutTargets = outgoing
        .filter((link) => String(link?.link_type || '').trim().toLowerCase() === 'test_out')
        .map((link) => resolveTargetId(link))
        .filter(Boolean) as string[];

      const defaultTargets = outgoing
        .filter((link) => String(link?.link_type || 'default').trim().toLowerCase() === 'default')
        .map((link) => resolveTargetId(link))
        .filter(Boolean) as string[];

      if (!testOutTargets.length || !defaultTargets.length) {
        continue;
      }

      const hasCompletedTestOutTarget = testOutTargets.some((targetId) =>
        completedItemIdSet.has(String(targetId))
      );

      if (!hasCompletedTestOutTarget) {
        continue;
      }

      for (const defaultTargetId of defaultTargets) {
        map[String(defaultTargetId)] = true;

        const descendants = collectDescendants(
          String(defaultTargetId),
          outgoingBySource,
          new Set<string>()
        );

        descendants.forEach((descendantId) => {
          if (String(descendantId) !== sourceId) {
            map[String(descendantId)] = true;
          }
        });
      }
    }

    return map;
  }, [items, links, completedItemIdSet]);

  const lockedByItemId = useMemo(() => {
    const map: Record<string, boolean> = {};
    const itemIds = new Set(items.map((item) => String(item.id)));

    items.forEach((item) => {
      map[String(item.id)] = false;
    });

    if (!isAssigned) {
      return map;
    }

    const incomingByTarget = new Map<string, any[]>();

    links.forEach((link) => {
      const sourceId = resolveSourceId(link);
      const targetId = resolveTargetId(link);

      if (!sourceId || !targetId) return;
      if (!itemIds.has(String(sourceId)) || !itemIds.has(String(targetId))) return;

      const existing = incomingByTarget.get(String(targetId)) || [];
      existing.push(link);
      incomingByTarget.set(String(targetId), existing);
    });

    items.forEach((item) => {
      const itemId = String(item.id);
      const itemType = String(item?.item_type || '').toLowerCase();

      if (itemType === 'section') {
        map[itemId] = false;
        return;
      }

      if (completionByItemId[itemId] || notApplicableByItemId[itemId]) {
        map[itemId] = false;
        return;
      }

      const incomingLinks = incomingByTarget.get(itemId) || [];

      if (!incomingLinks.length) {
        map[itemId] = false;
        return;
      }

      const hasSatisfiedIncoming = incomingLinks.some((link) => {
        const sourceId = resolveSourceId(link);
        if (!sourceId) return false;

        const sourceCompleted = !!completionByItemId[String(sourceId)];
        const sourceNotApplicable = !!notApplicableByItemId[String(sourceId)];

        return sourceCompleted || sourceNotApplicable;
      });

      map[itemId] = !hasSatisfiedIncoming;
    });

    return map;
  }, [items, links, isAssigned, completionByItemId, notApplicableByItemId]);

  const invalidateJourneyMapQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['my-journey-map-assignments', currentUserId] }),
      queryClient.invalidateQueries({ queryKey: ['journey-maps'] }),
      queryClient.invalidateQueries({ queryKey: ['journey-map-assignment-for-user', id, currentUserId] }),
      queryClient.invalidateQueries({ queryKey: ['journey-map-item-progress'] }),
      queryClient.invalidateQueries({ queryKey: ['journey-map-detail', id] }),
    ]);
  };

  const handleBack = async () => {
    navigate('/enablement');
  };

  /**
   * Launching an externally synced item (Mindtickle today) has to be recorded, because the
   * completion only arrives with the next nightly Snowflake load. Without the record the step
   * looks untouched until tomorrow; with it the learner sees "awaiting confirmation" straight
   * away. Re-launching an unconfirmed item re-stamps the record, which is what keeps the
   * displayed state as close to the truth as the nightly cadence allows.
   */
  const handleLaunchItem = async (item: any) => {
    const itemId = String(item?.id ?? '');
    const launchUrl = String(item?.launch_url || '');

    // Opened before any await: a window.open that happens after an async hop is no longer
    // attributable to the click and browsers block it as a popup.
    if (launchUrl) {
      window.open(launchUrl, '_blank', 'noopener,noreferrer');
    }

    if (!itemId || !shouldTrackExternalLaunch(item)) {
      return;
    }

    const ensuredAssignment =
      assignmentRecord || (await handleRequestAssignment().catch(() => null));

    if (!ensuredAssignment?.id) {
      return;
    }

    try {
      const result = await recordJourneyMapItemLaunch(item.id, ensuredAssignment.id);

      if (!launchUrl && result.launch_url) {
        window.open(result.launch_url, '_blank', 'noopener,noreferrer');
      }

      if (result.pending && !result.already_completed) {
        setLocalPendingValidationByItemId((prev) => ({
          ...prev,
          [itemId]: true,
        }));

        setLocalStatusReasonByItemId((prev) => ({
          ...prev,
          [itemId]:
            describeLaunchSyncState(result.sync_state) || AWAITING_CONFIRMATION_MESSAGE,
        }));
      }

      await itemProgressQuery.refetch();
    } catch {
      // Launch tracking is best effort. The learner is already in the course, so a failed
      // record must not read as a broken launch; the nightly sync still resolves completion.
    }
  };

  const syncAssignmentRecord = async (assignment: any) => {
    if (!assignment?.id) return null;

    const payload = {
      id: assignment.id,
      journey_map: assignment.journey_map,
      user: assignment.user,
      status: assignment.status,
      priority: assignment.priority,
      due_date: assignment.due_date,
      source_type: assignment.source_type,
      is_active: assignment.is_active,
      assigned_date: assignment.assigned_date,
    };

    return await putItem('db/journey-map-assignment', payload);
  };

  const handleRequestAssignment = async () => {
    if (!id) {
      throw new Error('Missing journey map id.');
    }

    if (!currentUserId) {
      throw new Error('Missing current user id.');
    }

    const existingRows = Array.isArray(assignmentQuery.data) ? assignmentQuery.data : [];
    const alreadyAssigned = existingRows.some((row: any) => isJourneyAccessibleAssignment(row));

    if (alreadyAssigned) {
      return assignmentRecord || existingRows[0] || true;
    }

    const payload = {
      journey_map: Number(id),
      user: currentUserId,
    };

    const created = await postItem('db/journey-map-assignment/', payload);

    await assignmentQuery.refetch();
    await invalidateJourneyMapQueries();

    return created;
  };

  const persistCompletedItemProgress = async (item: any, assignment: any) => {
    if (!currentUserId || !item?.id || !assignment?.id) return null;

    const nowIso = new Date().toISOString();
    let existing = itemProgressByItemId.get(String(item.id));

    if (!existing) {
      try {
        const rows = await fetchItems(
          'db/journey-map-item-progress/',
          {
            assignment: assignment.id,
            journey_map_item: item.id,
            user: currentUserId,
          },
          { includePageSize: false }
        );

        const matches = asArray(rows);
        existing = matches[0] || null;
      } catch {
        existing = null;
      }
    }

    if (existing?.id) {
      const alreadyFullySynced = isItemProgressFullySynced(
        existing,
        assignment.id,
        currentUserId,
        completedItemStatusId
      );

      if (alreadyFullySynced) {
        return existing;
      }

      const updatePayload = {
        id: existing.id,
        assignment: existing.assignment || assignment.id,
        journey_map_item: existing.journey_map_item || item.id,
        user: existing.user || currentUserId,
        status: completedItemStatusId ?? existing.status ?? null,
        path_state: PATH_STATE_COMPLETED,
        is_applicable: existing.is_applicable ?? true,
        path_decision_source: 'auto_complete_sync',
        started_date: existing.started_date ?? existing.last_viewed_date ?? nowIso,
        last_viewed_date: nowIso,
        completed_date: nowIso,
        score: existing.score ?? null,
        attempt_count: Math.max(Number(existing.attempt_count || 0), 1),
        completion_source: COMPLETION_SOURCE_EXTERNAL_SYNC,
        external_completion_id: existing.external_completion_id ?? null,
        external_status_payload: existing.external_status_payload ?? {},
        evidence: existing.evidence ?? {},
        sync_status: 'validated',
        sync_error: null,
        last_synced_at: nowIso,
        waived: existing.waived ?? false,
        waived_by: existing.waived_by ?? null,
        waived_date: existing.waived_date ?? null,
        override_reason: existing.override_reason ?? null,
        updated_by: String(currentUserId),
      };

      return await putItem('db/journey-map-item-progress', updatePayload);
    }

    const createPayload = {
      assignment: assignment.id,
      journey_map_item: item.id,
      user: currentUserId,
      status: completedItemStatusId,
      path_state: PATH_STATE_COMPLETED,
      is_applicable: true,
      path_decision_source: 'auto_complete_sync',
      started_date: nowIso,
      last_viewed_date: nowIso,
      completed_date: nowIso,
      score: null,
      attempt_count: 1,
      completion_source: COMPLETION_SOURCE_EXTERNAL_SYNC,
      external_completion_id: null,
      external_status_payload: {},
      evidence: {},
      sync_status: 'validated',
      sync_error: null,
      last_synced_at: nowIso,
      waived: false,
      waived_by: null,
      waived_date: null,
      override_reason: null,
      updated_by: String(currentUserId),
    };

    try {
      return await postItem('db/journey-map-item-progress/', createPayload);
    } catch (error: any) {
      let duplicateError = false;
      const nonFieldErrors =
        error?.response?.data?.non_field_errors ||
        error?.data?.non_field_errors ||
        [];

      if (Array.isArray(nonFieldErrors)) {
        duplicateError = nonFieldErrors.some((msg: string) =>
          String(msg).toLowerCase().includes('must make a unique set')
        );
      }

      if (!duplicateError) {
        throw error;
      }

      const rows = await fetchItems(
        'db/journey-map-item-progress/',
        {
          assignment: assignment.id,
          journey_map_item: item.id,
          user: currentUserId,
        },
        { includePageSize: false }
      );

      const matches = asArray(rows);
      const duplicateRow = matches[0];

      if (!duplicateRow?.id) {
        throw error;
      }

      const updatePayload = {
        id: duplicateRow.id,
        assignment: duplicateRow.assignment || assignment.id,
        journey_map_item: duplicateRow.journey_map_item || item.id,
        user: duplicateRow.user || currentUserId,
        status: completedItemStatusId ?? duplicateRow.status ?? null,
        path_state: PATH_STATE_COMPLETED,
        is_applicable: duplicateRow.is_applicable ?? true,
        path_decision_source: 'auto_complete_sync',
        started_date: duplicateRow.started_date ?? duplicateRow.last_viewed_date ?? nowIso,
        last_viewed_date: nowIso,
        completed_date: nowIso,
        score: duplicateRow.score ?? null,
        attempt_count: Math.max(Number(duplicateRow.attempt_count || 0), 1),
        completion_source: COMPLETION_SOURCE_EXTERNAL_SYNC,
        external_completion_id: duplicateRow.external_completion_id ?? null,
        external_status_payload: duplicateRow.external_status_payload ?? {},
        evidence: duplicateRow.evidence ?? {},
        sync_status: 'validated',
        sync_error: null,
        last_synced_at: nowIso,
        waived: duplicateRow.waived ?? false,
        waived_by: duplicateRow.waived_by ?? null,
        waived_date: duplicateRow.waived_date ?? null,
        override_reason: duplicateRow.override_reason ?? null,
        updated_by: String(currentUserId),
      };

      return await putItem('db/journey-map-item-progress', updatePayload);
    }
  };

  const finalizeCompletionPersistence = async (item: any, ensuredAssignment: any) => {
    if (!item?.id || !ensuredAssignment?.id) return;

    await persistCompletedItemProgress(item, ensuredAssignment);
    await itemProgressQuery.refetch();

    const refreshedAssignmentRows = await assignmentQuery.refetch();
    const refreshedRows = Array.isArray(refreshedAssignmentRows?.data)
      ? refreshedAssignmentRows.data
      : asArray(refreshedAssignmentRows?.data);

    const refreshedAssignment =
      refreshedRows.find((row: any) => String(row?.id) === String(ensuredAssignment.id)) ||
      ensuredAssignment;

    await syncAssignmentRecord(refreshedAssignment);
    await assignmentQuery.refetch();
    await invalidateJourneyMapQueries();
  };

  const handleCompleteItem = async (item: any) => {
    if (!item?.id) return;

    const itemId = String(item.id);

    setLocalCompletionByItemId((prev) => ({
      ...prev,
      [itemId]: true,
    }));

    setLocalPendingValidationByItemId((prev) => ({
      ...prev,
      [itemId]: false,
    }));

    const computedResult = completionQuery.data?.[itemId];
    if (computedResult?.reason) {
      setLocalCompletionReasonByItemId((prev) => ({
        ...prev,
        [itemId]: computedResult.reason || 'Completion has been satisfied for this journey item.',
      }));
    }

    const ensuredAssignment =
      assignmentRecord || (await handleRequestAssignment().catch(() => null));

    if (!ensuredAssignment?.id) {
      return;
    }

    await finalizeCompletionPersistence(item, ensuredAssignment);
  };

  /**
   * A self-hosted video crossed its watch-coverage threshold. The `video-progress` endpoint has
   * already written the completed row server-side, so this only reflects it in the UI and
   * refreshes derived state — it deliberately does NOT call `persistCompletedItemProgress`, which
   * would write a second row with an `external_sync` source.
   */
  const handleVideoCompleted = async (item: any) => {
    if (!item?.id) return;

    const itemId = String(item.id);

    setLocalCompletionByItemId((prev) => ({
      ...prev,
      [itemId]: true,
    }));

    setLocalPendingValidationByItemId((prev) => ({
      ...prev,
      [itemId]: false,
    }));

    await itemProgressQuery.refetch();
    await syncAssignmentRecord(assignmentRecord);
    await assignmentQuery.refetch();
    await invalidateJourneyMapQueries();
  };

  const handleTaskSubmissionSaved = async (item: any) => {
    if (!item?.id) return;

    const itemId = String(item.id);

    setLocalPendingValidationByItemId((prev) => ({
      ...prev,
      [itemId]: true,
    }));

    setLocalStatusReasonByItemId((prev) => ({
      ...prev,
      [itemId]: 'Your task submission was saved and is awaiting validation.',
    }));

    setLocalCompletionByItemId((prev) => ({
      ...prev,
      [itemId]: false,
    }));

    await itemProgressQuery.refetch();
    await invalidateJourneyMapQueries();
  };

  const handleRefreshItemCompletion = async (item: any): Promise<boolean> => {
    const result = await resolveCompletionForItem(item, email || '');
    const itemId = String(item?.id);

    if (!result.passed) {
      return false;
    }

    setLocalCompletionByItemId((prev) => ({
      ...prev,
      [itemId]: true,
    }));

    setLocalPendingValidationByItemId((prev) => ({
      ...prev,
      [itemId]: false,
    }));

    if (result.reason) {
      setLocalCompletionReasonByItemId((prev) => ({
        ...prev,
        [itemId]: result.reason,
      }));
    }

    const existing = itemProgressByItemId.get(itemId);
    const alreadyFullySynced =
      assignmentRecord?.id &&
      currentUserId &&
      isItemProgressFullySynced(
        existing,
        assignmentRecord.id,
        currentUserId,
        completedItemStatusId
      );

    if (alreadyFullySynced) {
      return true;
    }

    const ensuredAssignment =
      assignmentRecord || (await handleRequestAssignment().catch(() => null));

    if (ensuredAssignment?.id) {
      await finalizeCompletionPersistence(item, ensuredAssignment);
    }

    return true;
  };

  const renderCourseItem = (
    item: any,
    opts: {
      alreadyCompleted: boolean;
      completionReason?: string;
      onComplete: () => Promise<void> | void;
      onLaunchLearning?: () => void;
    }
  ) => {
    if (!isCourseProvider(item)) return null;

    return (
      <div style={{ minHeight: '420px' }}>
        <JourneyMapMoodleCourseModalContent
          item={item}
          returnTo={`/enablement/journey-map/${id}`}
          onLaunchLearning={opts.onLaunchLearning}
          completionReason={opts.completionReason}
          alreadyCompleted={opts.alreadyCompleted}
          onComplete={async () => {
            await opts.onComplete();
          }}
          onRefreshCompletion={async () => {
            return await handleRefreshItemCompletion(item);
          }}
        />
      </div>
    );
  };

  if (isLoading || (isFetching && !data)) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
        <HbrSpinner size="large">
          <div slot="label">Loading journey map...</div>
        </HbrSpinner>
      </div>
    );
  }

  if (error || !journeyMap) {
    return (
      <div className="hbr-css__layout-col-md">
        <div>
          <StbButton role="default" onClick={handleBack}>
            <span
              className="hbr-css__layout-row-3xs hbr-css__layout-align-center"
              style={{ gap: '8px' }}
            >
              <HbrIcon asset={arrowLeft} />
              <span>Back to Journey Maps</span>
            </span>
          </StbButton>
        </div>

        <HbrCard
          style={
            {
              '--box-shadow': 'none',
              '--border-width': '2px',
              '--border-radius': '8px',
            } as React.CSSProperties
          }
        >
          <div className="hbr-css__layout-col-3xs">
            <div className="hbr-css__text-primary-sm-semibold">Journey map not found.</div>
            <div className="hbr-css__text-secondary-sm">
              The requested journey map could not be loaded.
            </div>
          </div>
        </HbrCard>
      </div>
    );
  }

  return (
    <div className="hbr-css__layout-col-md">
      <div>
        <button
          type="button"
          onClick={handleBack}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            borderRadius: '999px',
            border: '1px solid var(--hbr-color-neutral-border-weak)',
            background: 'var(--hbr-color-neutral-bg)',
            color: 'var(--hbr-color-neutral-text)',
            cursor: 'pointer',
            font: 'inherit',
            fontWeight: 600,
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: '999px',
              display: 'grid',
              placeItems: 'center',
              background: 'var(--hbr-color-neutral-bg-medium)',
              flexShrink: 0,
            }}
          >
            <HbrIcon asset={arrowLeft} />
          </span>
          <span>Back to Journey Maps</span>
        </button>
      </div>

      <JourneyMapViewer
        journeyMap={journeyMap}
        items={itemsWithProgress}
        links={links}
        assignmentId={assignmentRecord?.id ?? null}
        currentUserId={currentUserId}
        completionByItemId={completionByItemId}
        completionReasonByItemId={completionReasonByItemId}
        pendingValidationByItemId={pendingValidationByItemId}
        syncStatusByItemId={syncStatusByItemId}
        statusReasonByItemId={statusReasonByItemId}
        lockedByItemId={lockedByItemId}
        notApplicableByItemId={notApplicableByItemId}
        isAssigned={isAssigned}
        onRequestAssignment={handleRequestAssignment}
        onLaunchItem={handleLaunchItem}
        onVideoCompleted={handleVideoCompleted}
        onCompleteItem={handleCompleteItem}
        onTaskSubmissionSaved={handleTaskSubmissionSaved}
        renderCourseItem={renderCourseItem}
      />

      <RelatedArchitectures mapId={journeyMap?.id ?? null} className="mt-6" />
    </div>
  );
};

export default JourneyMapDetailPage;